/*
  ## Search engines // => CRAWLER'S TYPE
  Google // => CRAWLER'S CATEGORY
    - `googlebot` // => CRAWLER
    - `adsbot-google`
*/

export const CRAWLER_CATEGORY_LABELS = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  'perplexity-ai': 'Perplexity AI',
  youcom: 'You.com',
  amazon: 'Amazon',
  semrush: 'Semrush',
  ahrefs: 'Ahrefs',
  'chrome-lighthouse': 'Chrome Lighthouse',
  'screaming-frog': 'Screaming Frog',
  oncrawl: 'OnCrawl',
  botify: 'Botify',
  lumar: 'Lumar',
  moz: 'Moz',
  meta: 'Meta',
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
  slack: 'Slack',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  discord: 'Discord',
  google: 'Google',
  bing: 'Bing',
  yandex: 'Yandex',
  yahoo: 'Yahoo',
  baidu: 'Baidu',
  naver: 'Naver',
  seznam: 'Seznam',
  qwant: 'Qwant',
  ecosia: 'Ecosia',
  duckduckgo: 'DuckDuckGo',
  apple: 'Apple',
  unknown: 'Others',
  grouped: 'Misc',
};

type CrawlerCategory = keyof typeof CRAWLER_CATEGORY_LABELS;

export const CRAWLER_CATEGORIES = Object.keys(CRAWLER_CATEGORY_LABELS).reduce((acc, key) => {
  const crawler = key as CrawlerCategory;
  acc[crawler] = crawler;
  return acc;
}, {} as Record<CrawlerCategory, CrawlerCategory>);

export const CRAWLER_TYPES = {
  Ai: 'ai',
  SEOTools: 'seo-tools',
  Social: 'social-media',
  SearchEngines: 'search-engines',
  Other: 'other',
} as const;

export type CrawlerType = typeof CRAWLER_TYPES[keyof typeof CRAWLER_TYPES];
export type ActiveCrawlerType = Exclude<CrawlerType, 'other'>;

export const CRAWLER_TYPES_LABELS: Record<CrawlerType, string> = {
  [CRAWLER_TYPES.SearchEngines]: 'Search Engines',
  [CRAWLER_TYPES.Social]: 'Social Media',
  [CRAWLER_TYPES.SEOTools]: 'SEO Tools',
  [CRAWLER_TYPES.Ai]: 'AI',
  [CRAWLER_TYPES.Other]: 'Others',
};

type CrawlerTypesToCategoriesMap = Record<ActiveCrawlerType, { asList: CrawlerCategory[] }>;

const CRAWLER_TYPES_TO_CATEGORIES_MAP: CrawlerTypesToCategoriesMap = {
  [CRAWLER_TYPES.Ai]: {
    asList: [
      CRAWLER_CATEGORIES.chatgpt,
      CRAWLER_CATEGORIES.claude,
      CRAWLER_CATEGORIES.gemini,
      CRAWLER_CATEGORIES['perplexity-ai'],
      CRAWLER_CATEGORIES.youcom,
      CRAWLER_CATEGORIES.amazon,
    ],
  },
  [CRAWLER_TYPES.SEOTools]: {
    asList: [
      CRAWLER_CATEGORIES.semrush,
      CRAWLER_CATEGORIES.ahrefs,
      CRAWLER_CATEGORIES['chrome-lighthouse'],
      CRAWLER_CATEGORIES['screaming-frog'],
      CRAWLER_CATEGORIES.oncrawl,
      CRAWLER_CATEGORIES.botify,
      CRAWLER_CATEGORIES.lumar,
      CRAWLER_CATEGORIES.moz,
    ],
  },
  [CRAWLER_TYPES.Social]: {
    asList: [
      CRAWLER_CATEGORIES.meta,
      CRAWLER_CATEGORIES.twitter,
      CRAWLER_CATEGORIES.linkedin,
      CRAWLER_CATEGORIES.slack,
      CRAWLER_CATEGORIES.pinterest,
      CRAWLER_CATEGORIES.tiktok,
      CRAWLER_CATEGORIES.discord,
    ],
  },
  [CRAWLER_TYPES.SearchEngines]: {
    asList: [
      CRAWLER_CATEGORIES.google,
      CRAWLER_CATEGORIES.bing,
      CRAWLER_CATEGORIES.yandex,
      CRAWLER_CATEGORIES.yahoo,
      CRAWLER_CATEGORIES.baidu,
      CRAWLER_CATEGORIES.naver,
      CRAWLER_CATEGORIES.seznam,
      CRAWLER_CATEGORIES.qwant,
      CRAWLER_CATEGORIES.ecosia,
      CRAWLER_CATEGORIES.duckduckgo,
      CRAWLER_CATEGORIES.apple,
    ],
  },
};

const CRAWLER_TYPES_REVERSE_MAP = Object.entries(CRAWLER_TYPES_TO_CATEGORIES_MAP).reduce<
  Record<string, ActiveCrawlerType>
>((acc, [type, data]) => {
  const typedCrawlerType = type as ActiveCrawlerType;
  data.asList.forEach((crawler) => (acc[crawler] = typedCrawlerType));
  return acc;
}, {});

const CRAWLER_CATEGORIES_MAP = {
  [CRAWLER_CATEGORIES.google]: [
    'googlebot',
    'adsbot-google',
    'apis-google',
    'mediapartners-google',
    'google-safety',
    'feedfetcher-google',
    'googleproducer',
    'google-site-verification',
    'google-inspectiontool',
  ],
  // SQL maps `adidxbot`, `bingpreview` and `microsoftpreview` into the
  // consolidated `bingbot` label rather than emitting them verbatim, so they
  // never appear in the `crawler` column and need no entry here (PRE-2581).
  [CRAWLER_CATEGORIES.bing]: ['bingbot'],
  [CRAWLER_CATEGORIES.yandex]: ['yandexbot', 'yabrowser'],
  [CRAWLER_CATEGORIES.yahoo]: ['yahoo'],
  [CRAWLER_CATEGORIES.baidu]: ['baiduspider'],
  [CRAWLER_CATEGORIES.naver]: ['naver'],
  [CRAWLER_CATEGORIES.seznam]: ['sznprohlizec', 'seznambot'],
  [CRAWLER_CATEGORIES.qwant]: ['qwantbot', 'qwantify'],
  [CRAWLER_CATEGORIES.ecosia]: ['ecosia'],
  [CRAWLER_CATEGORIES.duckduckgo]: ['duckduckbot', 'duckassistbot'],
  [CRAWLER_CATEGORIES.apple]: ['applebot'],
  // SQL maps `facebookbot`, `meta-externalagent`, `facebookcatalog` into
  // the consolidated `meta` label rather than emitting them verbatim,
  // so only `meta` and the pre-PRE-2580 legacy labels
  // (`facebookexternalhit`, `instagram`, `whatsapp`) ever appear in the
  // `crawler` column.
  [CRAWLER_CATEGORIES.meta]: ['facebookexternalhit', 'meta', 'instagram', 'whatsapp'],
  [CRAWLER_CATEGORIES.twitter]: ['twitter'],
  [CRAWLER_CATEGORIES.linkedin]: ['linkedinbot'],
  [CRAWLER_CATEGORIES.slack]: ['slackbot'],
  [CRAWLER_CATEGORIES.pinterest]: ['pinterestbot'],
  [CRAWLER_CATEGORIES.tiktok]: ['tiktok', 'bytespider'],
  [CRAWLER_CATEGORIES.discord]: ['discordbot'],
  [CRAWLER_CATEGORIES.semrush]: ['semrushbot'],
  // SQL maps `ahrefssiteaudit` into the consolidated `ahrefsbot` label, so it
  // never appears in the `crawler` column (PRE-2581).
  [CRAWLER_CATEGORIES.ahrefs]: ['ahrefsbot'],
  [CRAWLER_CATEGORIES['chrome-lighthouse']]: ['chrome-lighthouse'],
  [CRAWLER_CATEGORIES['screaming-frog']]: ['screaming-frog'],
  [CRAWLER_CATEGORIES.oncrawl]: ['oncrawlbot'],
  [CRAWLER_CATEGORIES.botify]: ['botifybot'],
  [CRAWLER_CATEGORIES.lumar]: ['deepcrawl', 'lumar'],
  [CRAWLER_CATEGORIES.moz]: ['rogerbot', 'dotbot'],
  // SQL maps `%oai-searchbot%` UAs into the consolidated `chatgpt`
  // label rather than emitting `oai-searchbot` verbatim, so it never
  // appears in the `crawler` column.
  [CRAWLER_CATEGORIES.chatgpt]: ['gptbot', 'chatgpt'],
  // SQL maps `claude-searchbot` into the consolidated `claudebot` label, so it
  // never appears in the `crawler` column (PRE-2581).
  [CRAWLER_CATEGORIES.claude]: ['claudebot', 'anthropic-ai', 'claude-web', 'claude-user'],
  [CRAWLER_CATEGORIES.gemini]: ['google-extended'],
  [CRAWLER_CATEGORIES['perplexity-ai']]: ['perplexitybot', 'perplexity-user'],
  [CRAWLER_CATEGORIES.amazon]: ['amazonbot'],
  [CRAWLER_CATEGORIES.youcom]: ['youbot'],
  [CRAWLER_CATEGORIES.unknown]: ['other'],
  [CRAWLER_CATEGORIES.grouped]: ['grouped'],
} as const;

export type CrawlerCategoryScheme = 'with-gemini' | 'without-gemini';

export const DEFAULT_CATEGORY_SCHEME: CrawlerCategoryScheme = 'with-gemini';

function typesWithoutGemini(typesMap: CrawlerTypesToCategoriesMap): CrawlerTypesToCategoriesMap {
  const ai = typesMap[CRAWLER_TYPES.Ai].asList.filter((category) => category !== CRAWLER_CATEGORIES.gemini);
  return { ...typesMap, [CRAWLER_TYPES.Ai]: { asList: ai } };
}

const CRAWLER_TYPES_TO_CATEGORIES_MAPS: Record<CrawlerCategoryScheme, CrawlerTypesToCategoriesMap> = {
  'with-gemini': CRAWLER_TYPES_TO_CATEGORIES_MAP,
  'without-gemini': typesWithoutGemini(CRAWLER_TYPES_TO_CATEGORIES_MAP),
};

type CrawlerCategoriesMap = Partial<Record<CrawlerCategory, readonly string[]>>;

function withoutGemini(categoriesMap: CrawlerCategoriesMap): CrawlerCategoriesMap {
  const { gemini: geminiCrawlers = [], ...otherCategories } = categoriesMap;
  return {
    ...otherCategories,
    [CRAWLER_CATEGORIES.unknown]: [...(categoriesMap[CRAWLER_CATEGORIES.unknown] ?? []), ...geminiCrawlers],
  };
}

function reverseMapOf(categoriesMap: CrawlerCategoriesMap): Record<string, CrawlerCategory> {
  return Object.entries(categoriesMap).reduce<Record<string, CrawlerCategory>>((acc, [type, data]) => {
    const crawlerCategory = type as CrawlerCategory;
    data.forEach((crawler) => (acc[crawler] = crawlerCategory));
    return acc;
  }, {});
}

const CRAWLER_CATEGORIES_MAPS: Record<CrawlerCategoryScheme, CrawlerCategoriesMap> = {
  'with-gemini': CRAWLER_CATEGORIES_MAP,
  'without-gemini': withoutGemini(CRAWLER_CATEGORIES_MAP),
};

const CRAWLER_CATEGORIES_REVERSE_MAPS: Record<CrawlerCategoryScheme, Record<string, CrawlerCategory>> = {
  'with-gemini': reverseMapOf(CRAWLER_CATEGORIES_MAPS['with-gemini']),
  'without-gemini': reverseMapOf(CRAWLER_CATEGORIES_MAPS['without-gemini']),
};

/**
 * Legacy category keys → current category. PRE-2580 consolidated
 * `facebook`, `instagram`, and `whatsapp` into a single `meta`
 * category. Users with saved filters or bookmarked URLs from before
 * the migration still send the legacy keys; mapping them here keeps
 * those filters working — without this alias the fallback in
 * `crawlers()` would forward the unknown key as a raw crawler id
 * (e.g. `crawler IN ('facebook')`) and silently produce empty
 * results.
 */
const LEGACY_CATEGORY_ALIASES: Readonly<Record<string, CrawlerCategory>> = {
  facebook: CRAWLER_CATEGORIES.meta,
  instagram: CRAWLER_CATEGORIES.meta,
  whatsapp: CRAWLER_CATEGORIES.meta,
};

// Keys come from URLs, so a plain lookup would resolve `constructor` or
// `toString` to Object.prototype's functions.
function ownValue<T>(map: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

export function currentCategoryKey(crawlerCategory: string): string {
  return ownValue(LEGACY_CATEGORY_ALIASES, crawlerCategory) ?? crawlerCategory;
}

const RETIRED_CATEGORY_SUCCESSORS: Record<
  CrawlerCategoryScheme,
  Readonly<Partial<Record<CrawlerCategory, CrawlerCategory>>>
> = {
  'with-gemini': {},
  'without-gemini': { [CRAWLER_CATEGORIES.gemini]: CRAWLER_CATEGORIES.unknown },
};

export function successorOfRetiredCategory(
  crawlerCategory: string,
  scheme: CrawlerCategoryScheme
): string | undefined {
  return ownValue(RETIRED_CATEGORY_SUCCESSORS[scheme], crawlerCategory);
}

export function isRetiredCategory(crawlerCategory: string, scheme: CrawlerCategoryScheme): boolean {
  return successorOfRetiredCategory(crawlerCategory, scheme) !== undefined;
}

export function crawlers(
  crawlerCategory: string,
  scheme: CrawlerCategoryScheme
): readonly string[] {
  return (
    ownValue<readonly string[]>(CRAWLER_CATEGORIES_MAPS[scheme], currentCategoryKey(crawlerCategory)) || [
      crawlerCategory,
    ]
  );
}

export function isActiveCrawlerType(key: string): key is ActiveCrawlerType {
  return ownValue(CRAWLER_TYPES_TO_CATEGORIES_MAP, key) !== undefined;
}

export const ACTIVE_CRAWLER_TYPES: ActiveCrawlerType[] = Object.values(CRAWLER_TYPES).filter(isActiveCrawlerType);

export function crawlerTypeCategories(
  type: ActiveCrawlerType,
  scheme: CrawlerCategoryScheme
): string[] {
  return CRAWLER_TYPES_TO_CATEGORIES_MAPS[scheme][type].asList;
}

export function crawlerCategory(crawler: string, scheme: CrawlerCategoryScheme): string {
  return ownValue(CRAWLER_CATEGORIES_REVERSE_MAPS[scheme], crawler) || crawler;
}

export function crawlerCategoryLabel(crawler: string): string {
  return ownValue<string>(CRAWLER_CATEGORY_LABELS, crawler) || crawler;
}

export function crawlerTypeLabel(crawlerType: string): string {
  return ownValue(CRAWLER_TYPES_LABELS, crawlerType) || crawlerType;
}

export function crawlerType(crawlerName: string): CrawlerType {
  return ownValue(CRAWLER_TYPES_REVERSE_MAP, crawlerName) || CRAWLER_TYPES.Other;
}

export function categorizeCrawlersData<T extends { crawler: string }>(
  data: T[],
  scheme: CrawlerCategoryScheme
): T[] {
  return data.map((item) => ({ ...item, crawler: crawlerCategory(item.crawler, scheme) }));
}

export function categorizeCrawlersList(
  crawlersList: string[],
  scheme: CrawlerCategoryScheme
): string[] {
  const crawlerCategoriesSet = new Set(crawlersList.map((crawlerName) => crawlerCategory(crawlerName, scheme)));
  return Array.from(crawlerCategoriesSet);
}

function namedCategoriesOf(scheme: CrawlerCategoryScheme): string[] {
  return ACTIVE_CRAWLER_TYPES.flatMap((type) => crawlerTypeCategories(type, scheme));
}

const NAMED_CRAWLER_CATEGORIES: Record<CrawlerCategoryScheme, string[]> = {
  'with-gemini': namedCategoriesOf('with-gemini'),
  'without-gemini': namedCategoriesOf('without-gemini'),
};

export function pickerCategories(
  everCrawled: string[],
  scheme: CrawlerCategoryScheme
): string[] {
  return Array.from(new Set([...NAMED_CRAWLER_CATEGORIES[scheme], ...everCrawled]));
}

export function deCategorizeCrawlersList(
  crawlerCategoriesList: string[],
  scheme: CrawlerCategoryScheme
): string[] {
  const expandedCrawlers = new Set(
    crawlerCategoriesList.map((crawlerCategoryName) => crawlers(crawlerCategoryName, scheme)).flat()
  );
  return Array.from(expandedCrawlers);
}
